import * as pc from "playcanvas";
import type { GameClient } from "../net/GameClient";
import { FirstPersonController } from "./FirstPersonController";
import { GrenadeEffects, type GrenadeKind } from "../effects/GrenadeEffects";
import { predictTrajectory } from "../effects/GrenadeProjectile";

/**
 * GrenadeController — owns charge/release/throw + green trajectory preview
 * when a grenade is the equipped weapon.
 *
 * Sprint 7 — the controller now sends a `grenade_throw` to the server
 * on release and stops simulating the projectile locally. The server's
 * `grenade_state` broadcasts drive the PeerGrenadeRenderer that paints
 * the marble in everyone's world (including the thrower's). The
 * detonate effect spawn moved to a server-driven `grenade_detonate`
 * listener so the explosion lands at the authoritative impact point.
 *
 * Hooks into FirstPersonController via:
 *   - `consumeFireEdge()` (consumes the LMB edge after release)
 *   - `isFireHeld()` / `isPointerLocked()`
 *   - `getCameraEntity()` for origin + look direction
 */

interface KindConfig {
  /** m/s released on a max-charge throw. */
  maxSpeed: number;
  /** m/s on a tap (no charge). */
  minSpeed: number;
  /** Time to reach max charge. */
  chargeSec: number;
  /** Vertical bias (radians) added to look pitch — gives a natural arc. */
  arcLiftDeg: number;
  /** Fuse time before detonation (ignored if detonateOnFloor). */
  fuseSec: number;
  /** True for molotov — explodes on first ground contact. */
  detonateOnFloor: boolean;
  /** Projectile colour. */
  color: pc.Color;
}

const CONFIGS: Record<GrenadeKind, KindConfig> = {
  he:         { maxSpeed: 22, minSpeed: 9, chargeSec: 0.85, arcLiftDeg: 6, fuseSec: 1.6, detonateOnFloor: false, color: new pc.Color(0.32, 0.42, 0.18) },
  flash:      { maxSpeed: 22, minSpeed: 9, chargeSec: 0.85, arcLiftDeg: 6, fuseSec: 1.4, detonateOnFloor: false, color: new pc.Color(0.85, 0.78, 0.72) },
  smoke:      { maxSpeed: 20, minSpeed: 8, chargeSec: 0.85, arcLiftDeg: 6, fuseSec: 1.2, detonateOnFloor: false, color: new pc.Color(0.55, 0.55, 0.58) },
  molotov:    { maxSpeed: 18, minSpeed: 7, chargeSec: 0.85, arcLiftDeg: 8, fuseSec: 4.0, detonateOnFloor: true,  color: new pc.Color(0.85, 0.32, 0.12) },
  // CT incendiary — same throw/fire behavior as the T molotov, but its
  // own wire kind so it keeps the metal-canister model instead of
  // rendering as the molotov bottle.
  incendiary: { maxSpeed: 18, minSpeed: 7, chargeSec: 0.85, arcLiftDeg: 8, fuseSec: 4.0, detonateOnFloor: true,  color: new pc.Color(0.55, 0.60, 0.64) },
};

export class GrenadeController {
  /**
   * Fraction of the player's own velocity a thrown grenade inherits —
   * CS:GO/CS2 momentum inheritance. This is what makes running throws
   * carry and JUMP THROWS actually gain distance (release while rising
   * adds upward velocity). Without it, jumping added nothing to a throw.
   * Applied identically to the trajectory preview and the wire velocity;
   * the server's anti-cheat clamp (34 m/s) budgets for it.
   */
  private static readonly VELOCITY_INHERIT = 0.65;

  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly fpc: FirstPersonController;
  private readonly effects: GrenadeEffects;
  /** Trajectory preview line: a chain of small green spheres. */
  private previewDots: pc.Entity[] = [];
  private chargeStart = 0;
  private isCharging = false;
  private wasFireHeld = false;
  private readonly tickFn: (dt: number) => void;

  constructor(app: pc.AppBase, client: GameClient, fpc: FirstPersonController) {
    this.app = app;
    this.client = client;
    this.fpc = fpc;
    this.effects = new GrenadeEffects(app, fpc.getCameraEntity());
    this.tickFn = (dt: number) => this.update(dt);
    this.app.on("update", this.tickFn);
  }

  destroy(): void {
    this.app.off("update", this.tickFn);
    this.effects.destroy();
    this.clearPreview();
  }

  /** Sprint 5 — expose the shared shake bus for camera + damage shake. */
  getEffects(): GrenadeEffects {
    return this.effects;
  }

  /** Public hook for FPC to ask "is the equipped weapon a grenade right now?". */
  isGrenadeEquipped(): GrenadeKind | null {
    const id = this.client.getLatestSelf()?.weaponId ?? "";
    // The equipped weapon id maps straight to its wire kind — including
    // CT `incendiary`, which is now its own kind (previously aliased to
    // `molotov`, which made the thrown canister render as a bottle).
    const wireKind = id as GrenadeKind;
    if (!CONFIGS[wireKind]) return null;
    return wireKind;
  }

  private update(dt: number): void {
    if (dt < 0) return;
    const kind = this.isGrenadeEquipped();
    if (!kind) {
      if (this.isCharging) this.cancel();
      return;
    }
    if (!this.fpc.isPointerLocked()) {
      if (this.isCharging) this.cancel();
      return;
    }
    if (document.body.classList.contains("buy-open")) {
      if (this.isCharging) this.cancel();
      return;
    }
    // Phase gate: no aiming / charging / throwing a grenade outside the
    // phases where the server will accept a throw. Buy (freezetime) and the VS
    // intro lock weapons — you can't pull a grenade before the round starts.
    // Open everywhere a throw is honored: `live` / `warmup`, the post-round
    // window (`between_rounds`) so a held nade can still be lobbed into the
    // aftermath, and `end` (the match-end grace — the server validates the
    // exact grace ticks via inMatchEndGrace, and the match overlay unlocks the
    // pointer right after). Null phase (pre-first-HUD) stays blocked.
    const phase = this.client.getMatchPhase();
    const canThrow =
      phase === "live" || phase === "warmup" || phase === "between_rounds" || phase === "end";
    if (!canThrow) {
      if (this.isCharging) this.cancel();
      return;
    }

    const held = this.fpc.isFireHeld();
    // Press → start charge.
    if (held && !this.wasFireHeld) {
      this.startCharge();
    }
    // Release → throw.
    if (!held && this.wasFireHeld && this.isCharging) {
      this.releaseThrow(kind);
    }
    // While charging → render preview.
    if (this.isCharging) this.renderPreview(kind);

    this.wasFireHeld = held;
  }

  private startCharge(): void {
    this.isCharging = true;
    this.chargeStart = performance.now();
  }

  private cancel(): void {
    this.isCharging = false;
    this.clearPreview();
  }

  private chargeAmount(kind: GrenadeKind): number {
    const cfg = CONFIGS[kind];
    const elapsed = (performance.now() - this.chargeStart) / 1000;
    return Math.min(1, elapsed / cfg.chargeSec);
  }

  private speedFor(kind: GrenadeKind, charge: number): number {
    const cfg = CONFIGS[kind];
    return cfg.minSpeed + (cfg.maxSpeed - cfg.minSpeed) * charge;
  }

  private originAndDirection(kind: GrenadeKind): {
    origin: pc.Vec3;
    velocity: pc.Vec3;
  } {
    const camera = this.fpc.getCameraEntity();
    const camPos = camera.getPosition();
    // Spawn slightly forward + below camera so the projectile reads as
    // coming "from the hand" rather than the eyes.
    const fwd = camera.forward;
    const right = camera.right;
    const origin = new pc.Vec3(
      camPos.x + fwd.x * 0.3 + right.x * 0.15,
      camPos.y + fwd.y * 0.3 + right.y * 0.15 - 0.12,
      camPos.z + fwd.z * 0.3 + right.z * 0.15,
    );
    // Direction: camera forward, lifted by arcLiftDeg about world up.
    const cfg = CONFIGS[kind];
    const liftRad = (cfg.arcLiftDeg * Math.PI) / 180;
    const cosL = Math.cos(liftRad);
    const sinL = Math.sin(liftRad);
    // Pitch up by liftRad: rotate fwd around horizontal-perpendicular axis.
    // Equivalent in world: fwd' = (fwd_x*cos, fwd_y*cos + sin, fwd_z*cos)
    //   approximation valid for any horizontal yaw since lift is +Y.
    const horizLen = Math.sqrt(fwd.x * fwd.x + fwd.z * fwd.z) || 1;
    const dirX = (fwd.x / horizLen) * cosL;
    const dirY = fwd.y * cosL + sinL;
    const dirZ = (fwd.z / horizLen) * cosL;
    // Renormalize.
    const dlen = Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ) || 1;
    const speed = this.speedFor(kind, this.chargeAmount(kind));
    const velocity = new pc.Vec3(
      (dirX / dlen) * speed,
      (dirY / dlen) * speed,
      (dirZ / dlen) * speed,
    );
    // CS-style momentum inheritance: add a fraction of the player's own
    // velocity (run + jump) so moving throws carry and jump throws reach
    // farther. The preview line uses the same velocity, so what you see
    // while charging is what the server simulates.
    const pv = this.fpc.getVelocity();
    const inherit = GrenadeController.VELOCITY_INHERIT;
    velocity.x += pv.x * inherit;
    velocity.y += pv.y * inherit;
    velocity.z += pv.z * inherit;
    return { origin, velocity };
  }

  // ── State Probe ───────────────────────────────────────────────────────

  getChargeState(): { charging: boolean; amount: number } {
    const kind = this.isGrenadeEquipped();
    if (!this.isCharging || !kind) return { charging: false, amount: 0 };
    return { charging: true, amount: this.chargeAmount(kind) };
  }

  // ── Preview line ──────────────────────────────────────────────────────

  private renderPreview(kind: GrenadeKind): void {
    const { origin, velocity } = this.originAndDirection(kind);
    // Up to 2 wall banks — matches the server sim (every kind, molotov
    // included, bounces off vertical faces), so bank lineups are aimable.
    const points = predictTrajectory(origin, velocity, 48, 0.05, 2);
    this.ensureDotCount(points.length);
    for (let i = 0; i < this.previewDots.length; i++) {
      const dot = this.previewDots[i];
      if (i < points.length) {
        const p = points[i];
        dot.enabled = true;
        dot.setPosition(p.x, p.y, p.z);
        // Fade dots toward the end; bigger near the player.
        const t = i / points.length;
        const s = 0.06 - t * 0.03;
        dot.setLocalScale(s, s, s);
      } else {
        dot.enabled = false;
      }
    }
  }

  private ensureDotCount(n: number): void {
    while (this.previewDots.length < n) {
      const e = new pc.Entity(`grenade-preview-${this.previewDots.length}`);
      e.addComponent("render", { type: "sphere" });
      const mat = new pc.StandardMaterial();
      mat.diffuse = new pc.Color(0, 0, 0);
      mat.emissive = new pc.Color(0.2, 1.0, 0.35);
      mat.emissiveIntensity = 3;
      mat.useLighting = false;
      mat.blendType = pc.BLEND_ADDITIVE;
      mat.depthWrite = false;
      mat.update();
      const r = e.render;
      if (r) { r.material = mat; r.castShadows = false; r.receiveShadows = false; }
      this.app.root.addChild(e);
      this.previewDots.push(e);
    }
  }

  private clearPreview(): void {
    for (const d of this.previewDots) d.destroy();
    this.previewDots = [];
  }

  // ── Throw ─────────────────────────────────────────────────────────────

  private releaseThrow(kind: GrenadeKind): void {
    const { origin, velocity } = this.originAndDirection(kind);
    this.isCharging = false;
    this.clearPreview();
    // Mark the LMB edge as consumed so FirstPersonController doesn't
    // also send a SHOOT input on the same press.
    this.fpc.consumeFireEdge();

    // Sprint 7 — server simulates the projectile. We forward the throw
    // vector and rely on `grenade_state` / `grenade_detonate` for the
    // visual feedback (PeerGrenadeRenderer + GrenadeEffects.spawn fire
    // through main.ts based on the detonate broadcast).
    this.client.sendGrenadeThrow({
      kind,
      ox: origin.x, oy: origin.y, oz: origin.z,
      vx: velocity.x, vy: velocity.y, vz: velocity.z,
    });
  }
}
